/**
 * Layout manager for Bharat Charts PHP — mirrors web/static/ki_charts_app.js.
 * Up to 3 named layouts, multi-grid presets, instant autosave, delete/close.
 */
// @ts-nocheck

export type LayoutHost = {
  getGrid: () => string;
  setGrid: (gridId: string, opts?: { silent?: boolean; fillAll?: boolean }) => void;
  getActivePane: () => string;
  setActivePane: (paneId: string) => void;
  getSymbols: () => {
    sector: string | null;
    stock: string | null;
    extras: (string | null)[];
  };
  openOnPane: (sym: string, paneId: string, opts?: { skipAutosave?: boolean; savedState?: any }) => void;
  fillVisiblePanes: (sym: string) => void;
  snapshotCharts: () => Promise<{
    sectorChart: any;
    stockChart: any;
    extraCharts: any[];
  }>;
  applyChartStates: (states: {
    sectorChart?: any;
    stockChart?: any;
    extraCharts?: any[];
  }, delayMs?: number) => void;
  getInterval: () => string;
  setInterval: (res: string) => void;
  bumpSize?: () => void;
  status?: (msg: string) => void;
};

var LAYOUTS_KEY = "bc_charts_layouts_v1";
var ACTIVE_LAYOUT_KEY = "bc_charts_active_layout";
var AUTOSAVE_PREF_KEY = "bc_charts_autosave_on";
var MAX_LAYOUTS = 3;
var AUTOSAVE_MS = 120;

function gridIcon(cells) {
  var rects = cells
    .map(function (c) {
      return (
        '<rect x="' +
        c.x +
        '" y="' +
        c.y +
        '" width="' +
        c.w +
        '" height="' +
        c.h +
        '" rx="1" fill="currentColor" opacity="0.85"/>'
      );
    })
    .join("");
  return '<svg viewBox="0 0 28 20" aria-hidden="true">' + rects + "</svg>";
}

/** TradingView-style multi-chart presets (1–8 panes). CSS maps each id in ki_charts.css. */
export var GRID_PRESETS = [
  { id: "1", label: "1 chart", panes: 1, icon: gridIcon([{ x: 4, y: 2, w: 20, h: 16 }]) },

  { id: "2", label: "2 side by side", panes: 2, icon: gridIcon([{ x: 2, y: 2, w: 11, h: 16 }, { x: 15, y: 2, w: 11, h: 16 }]) },
  { id: "2v", label: "2 stacked", panes: 2, icon: gridIcon([{ x: 4, y: 1, w: 20, h: 8 }, { x: 4, y: 11, w: 20, h: 8 }]) },

  { id: "3v", label: "3 columns", panes: 3, icon: gridIcon([{ x: 1, y: 2, w: 8, h: 16 }, { x: 10, y: 2, w: 8, h: 16 }, { x: 19, y: 2, w: 8, h: 16 }]) },
  { id: "3s", label: "3 rows", panes: 3, icon: gridIcon([{ x: 4, y: 1, w: 20, h: 5 }, { x: 4, y: 7.5, w: 20, h: 5 }, { x: 4, y: 14, w: 20, h: 5 }]) },
  { id: "3", label: "3 left + stack", panes: 3, icon: gridIcon([{ x: 2, y: 2, w: 12, h: 16 }, { x: 16, y: 2, w: 10, h: 7 }, { x: 16, y: 11, w: 10, h: 7 }]) },
  { id: "3r", label: "3 right + stack", panes: 3, icon: gridIcon([{ x: 2, y: 2, w: 10, h: 7 }, { x: 2, y: 11, w: 10, h: 7 }, { x: 14, y: 2, w: 12, h: 16 }]) },
  { id: "3h", label: "3 top + split", panes: 3, icon: gridIcon([{ x: 2, y: 1, w: 24, h: 8 }, { x: 2, y: 11, w: 11, h: 8 }, { x: 15, y: 11, w: 11, h: 8 }]) },

  { id: "4", label: "4 (2×2)", panes: 4, icon: gridIcon([{ x: 2, y: 1, w: 11, h: 8 }, { x: 15, y: 1, w: 11, h: 8 }, { x: 2, y: 11, w: 11, h: 8 }, { x: 15, y: 11, w: 11, h: 8 }]) },
  { id: "4v", label: "4 columns", panes: 4, icon: gridIcon([{ x: 1, y: 2, w: 6, h: 16 }, { x: 8, y: 2, w: 6, h: 16 }, { x: 15, y: 2, w: 6, h: 16 }, { x: 22, y: 2, w: 5, h: 16 }]) },
  { id: "4h", label: "4 rows", panes: 4, icon: gridIcon([{ x: 4, y: 1, w: 20, h: 4 }, { x: 4, y: 6, w: 20, h: 4 }, { x: 4, y: 11, w: 20, h: 4 }, { x: 4, y: 16, w: 20, h: 3 }]) },
  { id: "4l", label: "4 left + 3", panes: 4, icon: gridIcon([{ x: 1, y: 1, w: 12, h: 18 }, { x: 15, y: 1, w: 12, h: 5 }, { x: 15, y: 7.5, w: 12, h: 5 }, { x: 15, y: 14, w: 12, h: 5 }]) },
  { id: "4r", label: "4 right + 3", panes: 4, icon: gridIcon([{ x: 1, y: 1, w: 12, h: 5 }, { x: 1, y: 7.5, w: 12, h: 5 }, { x: 1, y: 14, w: 12, h: 5 }, { x: 15, y: 1, w: 12, h: 18 }]) },
  { id: "4t", label: "4 top + 3", panes: 4, icon: gridIcon([{ x: 1, y: 1, w: 26, h: 8 }, { x: 1, y: 11, w: 8, h: 8 }, { x: 10, y: 11, w: 8, h: 8 }, { x: 19, y: 11, w: 8, h: 8 }]) },
  { id: "4b", label: "4 bottom + 3", panes: 4, icon: gridIcon([{ x: 1, y: 1, w: 8, h: 8 }, { x: 10, y: 1, w: 8, h: 8 }, { x: 19, y: 1, w: 8, h: 8 }, { x: 1, y: 11, w: 26, h: 8 }]) },

  { id: "5a", label: "5 (2+3)", panes: 5, icon: gridIcon([{ x: 1, y: 1, w: 13, h: 8 }, { x: 15, y: 1, w: 12, h: 8 }, { x: 1, y: 11, w: 8, h: 8 }, { x: 10, y: 11, w: 8, h: 8 }, { x: 19, y: 11, w: 8, h: 8 }]) },
  { id: "5b", label: "5 (3+2)", panes: 5, icon: gridIcon([{ x: 1, y: 1, w: 8, h: 8 }, { x: 10, y: 1, w: 8, h: 8 }, { x: 19, y: 1, w: 8, h: 8 }, { x: 1, y: 11, w: 13, h: 8 }, { x: 15, y: 11, w: 12, h: 8 }]) },
  { id: "5l", label: "5 left + 4", panes: 5, icon: gridIcon([{ x: 1, y: 1, w: 11, h: 18 }, { x: 14, y: 1, w: 6, h: 8 }, { x: 21, y: 1, w: 6, h: 8 }, { x: 14, y: 11, w: 6, h: 8 }, { x: 21, y: 11, w: 6, h: 8 }]) },
  { id: "5r", label: "5 right + 4", panes: 5, icon: gridIcon([{ x: 1, y: 1, w: 6, h: 8 }, { x: 8, y: 1, w: 6, h: 8 }, { x: 1, y: 11, w: 6, h: 8 }, { x: 8, y: 11, w: 6, h: 8 }, { x: 16, y: 1, w: 11, h: 18 }]) },

  { id: "6", label: "6 (3×2)", panes: 6, icon: gridIcon([{ x: 1, y: 1, w: 8, h: 8 }, { x: 10, y: 1, w: 8, h: 8 }, { x: 19, y: 1, w: 8, h: 8 }, { x: 1, y: 11, w: 8, h: 8 }, { x: 10, y: 11, w: 8, h: 8 }, { x: 19, y: 11, w: 8, h: 8 }]) },
  { id: "6v", label: "6 (2×3)", panes: 6, icon: gridIcon([{ x: 1, y: 1, w: 13, h: 5 }, { x: 15, y: 1, w: 12, h: 5 }, { x: 1, y: 7.5, w: 13, h: 5 }, { x: 15, y: 7.5, w: 12, h: 5 }, { x: 1, y: 14, w: 13, h: 5 }, { x: 15, y: 14, w: 12, h: 5 }]) },
  { id: "6l", label: "6 left + 5", panes: 6, icon: gridIcon([{ x: 1, y: 1, w: 10, h: 18 }, { x: 13, y: 1, w: 7, h: 5 }, { x: 21, y: 1, w: 6, h: 5 }, { x: 13, y: 7.5, w: 7, h: 5 }, { x: 21, y: 7.5, w: 6, h: 5 }, { x: 13, y: 14, w: 14, h: 5 }]) },
  { id: "6r", label: "6 right + 5", panes: 6, icon: gridIcon([{ x: 1, y: 1, w: 7, h: 5 }, { x: 9, y: 1, w: 6, h: 5 }, { x: 1, y: 7.5, w: 7, h: 5 }, { x: 9, y: 7.5, w: 6, h: 5 }, { x: 1, y: 14, w: 14, h: 5 }, { x: 17, y: 1, w: 10, h: 18 }]) },

  { id: "7a", label: "7 (3+4)", panes: 7, icon: gridIcon([{ x: 1, y: 1, w: 8, h: 8 }, { x: 10, y: 1, w: 8, h: 8 }, { x: 19, y: 1, w: 8, h: 8 }, { x: 1, y: 11, w: 6, h: 8 }, { x: 8, y: 11, w: 6, h: 8 }, { x: 15, y: 11, w: 6, h: 8 }, { x: 22, y: 11, w: 5, h: 8 }]) },
  { id: "7b", label: "7 (4+3)", panes: 7, icon: gridIcon([{ x: 1, y: 1, w: 6, h: 8 }, { x: 8, y: 1, w: 6, h: 8 }, { x: 15, y: 1, w: 6, h: 8 }, { x: 22, y: 1, w: 5, h: 8 }, { x: 1, y: 11, w: 8, h: 8 }, { x: 10, y: 11, w: 8, h: 8 }, { x: 19, y: 11, w: 8, h: 8 }]) },
  { id: "7l", label: "7 left + 6", panes: 7, icon: gridIcon([{ x: 1, y: 1, w: 9, h: 18 }, { x: 12, y: 1, w: 5, h: 5 }, { x: 18, y: 1, w: 4, h: 5 }, { x: 23, y: 1, w: 4, h: 5 }, { x: 12, y: 7.5, w: 5, h: 5 }, { x: 18, y: 7.5, w: 4, h: 5 }, { x: 23, y: 7.5, w: 4, h: 5 }]) },

  { id: "8", label: "8 (4×2)", panes: 8, icon: gridIcon([{ x: 1, y: 1, w: 6, h: 8 }, { x: 8, y: 1, w: 6, h: 8 }, { x: 15, y: 1, w: 6, h: 8 }, { x: 22, y: 1, w: 5, h: 8 }, { x: 1, y: 11, w: 6, h: 8 }, { x: 8, y: 11, w: 6, h: 8 }, { x: 15, y: 11, w: 6, h: 8 }, { x: 22, y: 11, w: 5, h: 8 }]) },
  { id: "8v", label: "8 (2×4)", panes: 8, icon: gridIcon([{ x: 1, y: 1, w: 13, h: 4 }, { x: 15, y: 1, w: 12, h: 4 }, { x: 1, y: 6, w: 13, h: 4 }, { x: 15, y: 6, w: 12, h: 4 }, { x: 1, y: 11, w: 13, h: 4 }, { x: 15, y: 11, w: 12, h: 4 }, { x: 1, y: 16, w: 13, h: 3 }, { x: 15, y: 16, w: 12, h: 3 }]) },
  { id: "8l", label: "8 left + 7", panes: 8, icon: gridIcon([{ x: 1, y: 1, w: 9, h: 18 }, { x: 12, y: 1, w: 5, h: 5 }, { x: 18, y: 1, w: 4, h: 5 }, { x: 23, y: 1, w: 4, h: 5 }, { x: 12, y: 7.5, w: 5, h: 5 }, { x: 18, y: 7.5, w: 4, h: 5 }, { x: 23, y: 7.5, w: 4, h: 5 }, { x: 12, y: 14, w: 15, h: 5 }]) },
  { id: "8r", label: "8 right + 7", panes: 8, icon: gridIcon([{ x: 1, y: 1, w: 5, h: 5 }, { x: 7, y: 1, w: 4, h: 5 }, { x: 12, y: 1, w: 4, h: 5 }, { x: 1, y: 7.5, w: 5, h: 5 }, { x: 7, y: 7.5, w: 4, h: 5 }, { x: 12, y: 7.5, w: 4, h: 5 }, { x: 1, y: 14, w: 15, h: 5 }, { x: 18, y: 1, w: 9, h: 18 }]) },
];

export var VALID_GRIDS = {};
GRID_PRESETS.forEach(function (p) {
  VALID_GRIDS[p.id] = 1;
});

var ALL_PANES = ["sector", "stock", "extra0", "extra1", "extra2", "extra3", "extra4", "extra5"];

export function panesForGrid(gridId) {
  var g = gridId || "1";
  var preset = null;
  for (var i = 0; i < GRID_PRESETS.length; i++) {
    if (GRID_PRESETS[i].id === g) {
      preset = GRID_PRESETS[i];
      break;
    }
  }
  var n = preset ? preset.panes : 1;
  return ALL_PANES.slice(0, Math.max(1, Math.min(8, n)));
}

function uid() {
  return "lay_" + Date.now().toString(36) + "_" + Math.random().toString(36).slice(2, 8);
}

function readJson(key, fallback) {
  try {
    var raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch (_) {
    return fallback;
  }
}

function writeJson(key, val) {
  try {
    localStorage.setItem(key, JSON.stringify(val));
  } catch (_) {}
}

/** ?symbol= on the page URL (a link from the option chain, watchlist, share) — wins over the saved layout. */
function urlSymbol() {
  try {
    // The page mirrors the current symbol into the URL, so a reload's ?symbol= is not a new link.
    var nav = performance.getEntriesByType && performance.getEntriesByType("navigation")[0];
    if (nav && (nav.type === "reload" || nav.type === "back_forward")) return "";
    var s = new URLSearchParams(location.search).get("symbol");
    return s && s.trim() ? s.trim() : "";
  } catch (_) {
    return "";
  }
}

/** Copy of a saved TV chart state with its main symbol swapped, so studies and drawings survive. */
function retargetChartState(state, fromSym, toSym) {
  if (!state || !fromSym || !toSym) return state;
  var norm = function (v) {
    return String(v || "").replace(/^[^:]*:(?=\S)/, "").trim().toUpperCase();
  };
  var from = norm(fromSym);
  var keys = { symbol: 1, shortName: 1, short_name: 1, pro_name: 1, ticker: 1 };
  try {
    var copy = JSON.parse(JSON.stringify(state));
    (function walk(o) {
      if (!o || typeof o !== "object") return;
      for (var k in o) {
        var v = o[k];
        if (typeof v === "string" && keys[k] && norm(v) === from) o[k] = toSym;
        else if (v && typeof v === "object") walk(v);
      }
    })(copy);
    return copy;
  } catch (_) {
    return null;
  }
}

function presetLabel(id) {
  for (var i = 0; i < GRID_PRESETS.length; i++) {
    if (GRID_PRESETS[i].id === id) return GRID_PRESETS[i].label;
  }
  return "1 chart";
}

function intervalLabel(res) {
  var r = String(res || "5");
  if (r === "1D" || r === "D") return "1D";
  if (r === "1W" || r === "W") return "1W";
  return r + "m";
}

export function initLayouts(host: LayoutHost) {
  var layouts = [];
  var activeLayoutId = null;
  var autosaveTimer = null;
  var suppressAutosave = false;
  // Strict: always ON. Ignore any prior Off preference so saves + toast always fire.
  var autoSaveOn = true;
  try {
    localStorage.setItem(AUTOSAVE_PREF_KEY, "1");
  } catch (_) {}
  var newLayoutGridId = "1";

  var layoutChipsEl = document.getElementById("chartsLayoutChips");
  var settingsBtn = document.getElementById("chartsLayoutSettingsBtn");
  var settingsMenu = document.getElementById("chartsLayoutSettingsMenu");
  var settingsBackdrop = document.getElementById("chartsLayoutSettingsBackdrop");
  var settingsTitle = document.getElementById("chartsSettingsMenuTitle");
  var settingsSub = document.getElementById("chartsSettingsMenuSub");
  var settingsGridPresets = document.getElementById("chartsSettingsGridPresets");
  var autosaveBadge = document.getElementById("chartsAutosaveBadge");
  var autosaveToast = document.getElementById("chartsAutosaveToast");
  var newLayoutBtn = document.getElementById("chartsNewLayoutBtn");
  var newLayoutModal = document.getElementById("chartsNewLayoutModal");
  var newLayoutName = document.getElementById("chartsNewLayoutName");
  var newLayoutPresets = document.getElementById("chartsNewLayoutPresets");
  var newLayoutHint = document.getElementById("chartsNewLayoutHint");
  var renameModal = document.getElementById("chartsRenameModal");
  var renameInput = document.getElementById("chartsRenameInput");

  function status(msg) {
    if (host.status) host.status(msg);
  }

  function persistLayouts() {
    writeJson(LAYOUTS_KEY, layouts);
    if (activeLayoutId) localStorage.setItem(ACTIVE_LAYOUT_KEY, activeLayoutId);
    else localStorage.removeItem(ACTIVE_LAYOUT_KEY);
  }

  function activeLayout() {
    if (!activeLayoutId) return null;
    for (var i = 0; i < layouts.length; i++) {
      if (layouts[i].id === activeLayoutId) return layouts[i];
    }
    return null;
  }

  function loadLayouts() {
    var arr = readJson(LAYOUTS_KEY, []);
    layouts = Array.isArray(arr) ? arr : [];
    if (layouts.length > MAX_LAYOUTS) layouts = layouts.slice(0, MAX_LAYOUTS);
    activeLayoutId = localStorage.getItem(ACTIVE_LAYOUT_KEY) || null;
    if (activeLayoutId && !layouts.some(function (l) { return l.id === activeLayoutId; })) {
      activeLayoutId = layouts.length ? layouts[0].id : null;
      if (activeLayoutId) localStorage.setItem(ACTIVE_LAYOUT_KEY, activeLayoutId);
      else localStorage.removeItem(ACTIVE_LAYOUT_KEY);
    }
    ensureDefaultLayout();
  }

  function ensureDefaultLayout() {
    if (layouts.length && activeLayoutId) return activeLayout();
    if (!layouts.length) {
      var syms = host.getSymbols();
      var lay = {
        id: uid(),
        name: "Layout 1",
        grid: host.getGrid() || "1",
        sector: syms.sector || null,
        stock: syms.stock || null,
        extras: (syms.extras || []).slice(),
        activePane: host.getActivePane() || "sector",
        sectorChart: null,
        stockChart: null,
        extraCharts: [null, null, null, null, null, null],
        interval: host.getInterval() || "5",
        updatedAt: Date.now(),
      };
      layouts.push(lay);
      activeLayoutId = lay.id;
      persistLayouts();
      renderLayoutChips();
      return lay;
    }
    if (!activeLayoutId) {
      activeLayoutId = layouts[0].id;
      persistLayouts();
      renderLayoutChips();
    }
    return activeLayout();
  }

  function updateAutoTags() {
    var tag = document.getElementById("chartsAutoTag");
    var toggleTag = document.getElementById("chartsAutoToggleTag");
    if (tag) {
      tag.textContent = "AUTO";
      tag.classList.toggle("is-off", !autoSaveOn);
    }
    if (toggleTag) {
      toggleTag.textContent = autoSaveOn ? "ON" : "OFF";
      toggleTag.classList.toggle("is-off", !autoSaveOn);
    }
  }

  function setAutoSave(_on) {
    // Strict: always ON — ignore attempts to disable.
    autoSaveOn = true;
    try {
      localStorage.setItem(AUTOSAVE_PREF_KEY, "1");
    } catch (_) {}
    updateAutoTags();
    updateSettingsMenuHead();
  }

  function showAutosaveBadge(_label) {
    // Header autosave badge removed — keep saves silent.
    if (!autosaveToast) autosaveToast = document.getElementById("chartsAutosaveToast");
    if (autosaveToast) {
      autosaveToast.setAttribute("hidden", "");
      autosaveToast.classList.remove("is-show", "is-saving", "is-saved");
    }
  }

  /** Toast-only (watchlist / favorites / prefs) — already persisted elsewhere. */
  function notifySaved(label) {
    var t = label || "Saved";
    // Keep short for header
    if (/watchlist/i.test(t)) t = "WL saved";
    else if (/favorit/i.test(t)) t = "Favs saved";
    else if (/theme/i.test(t)) t = "Theme saved";
    else if (/^✓\s*/.test(t)) t = t.replace(/^✓\s*/, "");
    showAutosaveBadge(t);
  }

  var pendingSaveReason = "";
  function scheduleAutosave(reason) {
    ensureDefaultLayout();
    if (suppressAutosave || !activeLayoutId) return;
    // Strict: always save — preference cannot disable.
    autoSaveOn = true;
    if (reason) pendingSaveReason = String(reason);
    showAutosaveBadge("Saving…");
    clearTimeout(autosaveTimer);
    autosaveTimer = setTimeout(function () {
      saveActiveLayout(true);
    }, AUTOSAVE_MS);
  }

  function saveActiveLayout(isAuto) {
    ensureDefaultLayout();
    var lay = activeLayout();
    if (!lay) {
      showAutosaveBadge("Save failed");
      return Promise.resolve();
    }
    showAutosaveBadge("Saving…");
    var snapPromise;
    try {
      snapPromise = host.snapshotCharts();
    } catch (_) {
      showAutosaveBadge("Save failed");
      return Promise.resolve();
    }
    if (!snapPromise || typeof snapPromise.then !== "function") {
      snapPromise = Promise.resolve({ sectorChart: null, stockChart: null, extraCharts: [] });
    }
    // Never hang on "Saving…" if widget.save stalls
    var timed = Promise.race([
      snapPromise,
      new Promise(function (resolve) {
        setTimeout(function () {
          resolve({ sectorChart: null, stockChart: null, extraCharts: [], _timedOut: true });
        }, 4000);
      }),
    ]);
    return timed
      .then(function (charts) {
        var syms = host.getSymbols();
        lay.grid = host.getGrid() || lay.grid || "1";
        lay.sector = syms.sector || null;
        lay.stock = syms.stock || null;
        lay.extras = (syms.extras || []).slice();
        lay.activePane = host.getActivePane() || "sector";
        lay.interval = host.getInterval() || "5";
        if (charts && charts.sectorChart) lay.sectorChart = charts.sectorChart;
        if (charts && charts.stockChart) lay.stockChart = charts.stockChart;
        if (charts && Array.isArray(charts.extraCharts)) {
          var hasExtra = charts.extraCharts.some(function (s) {
            return !!s;
          });
          if (hasExtra) lay.extraCharts = charts.extraCharts;
        }
        lay.updatedAt = Date.now();
        persistLayouts();
        renderLayoutChips();
        updateSettingsMenuHead();
        var why = pendingSaveReason;
        pendingSaveReason = "";
        // Keep header badge short
        var ok = "Saved";
        if (why === "watchlist") ok = "WL saved";
        else if (why === "indicator") ok = "Saved";
        showAutosaveBadge(ok);
        if (!isAuto) status("Template saved");
      })
      .catch(function () {
        // Still persist symbol/grid metadata even if chart snapshot fails
        try {
          var syms2 = host.getSymbols();
          lay.grid = host.getGrid() || lay.grid || "1";
          lay.sector = syms2.sector || null;
          lay.stock = syms2.stock || null;
          lay.extras = (syms2.extras || []).slice();
          lay.interval = host.getInterval() || "5";
          lay.updatedAt = Date.now();
          persistLayouts();
        } catch (_) {}
        pendingSaveReason = "";
        showAutosaveBadge("✓ Saved");
      });
  }

  function renderLayoutChips() {
    if (!layoutChipsEl) return;
    if (!layouts.length) {
      layoutChipsEl.innerHTML =
        '<span style="font-size:0.68rem;color:#64748b;white-space:nowrap;">No saved layouts</span>';
      return;
    }
    layoutChipsEl.innerHTML = layouts
      .map(function (l) {
        var active = l.id === activeLayoutId ? " is-active" : "";
        return (
          '<button type="button" class="cx-layout-chip' +
          active +
          '" data-layout-id="' +
          String(l.id).replace(/"/g, "") +
          '" title="' +
          String(l.name || "").replace(/"/g, "") +
          '">' +
          (l.name || "Layout") +
          "</button>"
        );
      })
      .join("");
  }

  function updateSettingsMenuHead() {
    var lay = activeLayout();
    if (settingsTitle) settingsTitle.textContent = lay ? lay.name || "Layout" : "Layout";
    if (settingsSub) {
      var bits = [];
      if (lay) {
        bits.push(presetLabel(lay.grid));
        bits.push(autoSaveOn ? "auto-saves" : "manual save");
        bits.push(layouts.length + "/" + MAX_LAYOUTS);
        if (lay.sector) bits.push(lay.sector);
      } else {
        bits.push("Create or open a layout");
      }
      settingsSub.textContent = bits.join(" · ");
    }
    updateAutoTags();
  }

  function renderGridPresetButtons(hostEl, activeId, onPick) {
    if (!hostEl) return;
    var byCount = {};
    GRID_PRESETS.forEach(function (p) {
      var n = p.panes || 1;
      if (!byCount[n]) byCount[n] = [];
      byCount[n].push(p);
    });
    var html = "";
    Object.keys(byCount)
      .map(Number)
      .sort(function (a, b) {
        return a - b;
      })
      .forEach(function (n) {
        html += '<div class="cx-grid-row" data-panes="' + n + '">';
        byCount[n].forEach(function (p) {
          var cls = p.id === activeId ? " cx-grid-btn is-active" : " cx-grid-btn";
          html +=
            '<button type="button" class="' +
            cls.trim() +
            '" data-grid="' +
            p.id +
            '" title="' +
            p.label +
            '">' +
            p.icon +
            '<span class="cx-grid-caption">' +
            p.label +
            "</span></button>";
        });
        html += "</div>";
      });
    hostEl.innerHTML = html;
    hostEl.querySelectorAll("[data-grid]").forEach(function (btn) {
      btn.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        onPick(btn.getAttribute("data-grid"));
      });
    });
  }

  function openSettingsMenu() {
    if (!settingsMenu) return;
    settingsMenu.classList.add("is-open");
    if (settingsBackdrop) {
      settingsBackdrop.hidden = false;
      settingsBackdrop.classList.add("is-open");
      settingsBackdrop.setAttribute("aria-hidden", "false");
    }
    if (settingsBtn) {
      settingsBtn.setAttribute("aria-expanded", "true");
      settingsBtn.classList.add("is-open");
    }
    updateSettingsMenuHead();
    renderGridPresetButtons(settingsGridPresets, host.getGrid(), function (g) {
      host.setGrid(g);
      scheduleAutosave();
      closeSettingsMenu();
    });
  }

  function closeSettingsMenu() {
    if (settingsMenu) settingsMenu.classList.remove("is-open");
    if (settingsBackdrop) {
      settingsBackdrop.classList.remove("is-open");
      settingsBackdrop.hidden = true;
      settingsBackdrop.setAttribute("aria-hidden", "true");
    }
    if (settingsBtn) {
      settingsBtn.setAttribute("aria-expanded", "false");
      settingsBtn.classList.remove("is-open");
    }
  }

  function openModal(el) {
    if (!el) return;
    el.hidden = false;
    el.classList.add("is-open");
  }

  function closeModal(el) {
    if (!el) return;
    el.classList.remove("is-open");
    el.hidden = true;
  }

  function refreshNewLayoutPresets() {
    if (newLayoutHint) newLayoutHint.textContent = presetLabel(newLayoutGridId);
    renderGridPresetButtons(newLayoutPresets, newLayoutGridId, function (g) {
      newLayoutGridId = g;
      refreshNewLayoutPresets();
    });
  }

  function openNewLayoutModal() {
    if (layouts.length >= MAX_LAYOUTS) {
      status("Max " + MAX_LAYOUTS + " layouts — delete one first");
      return;
    }
    newLayoutGridId = host.getGrid() || "1";
    if (newLayoutName) newLayoutName.value = "";
    refreshNewLayoutPresets();
    openModal(newLayoutModal);
    setTimeout(function () {
      if (newLayoutName) newLayoutName.focus();
    }, 30);
  }

  function createLayout(name, gridId) {
    if (layouts.length >= MAX_LAYOUTS) {
      status("Max " + MAX_LAYOUTS + " layouts");
      return;
    }
    var n = String(name || "").trim() || "Layout " + (layouts.length + 1);
    var syms = host.getSymbols();
    var lay = {
      id: uid(),
      name: n,
      grid: gridId || "1",
      sector: syms.sector || null,
      stock: syms.stock || null,
      extras: (syms.extras || []).slice(),
      activePane: host.getActivePane() || "sector",
      sectorChart: null,
      stockChart: null,
      extraCharts: [null, null, null, null, null, null],
      interval: host.getInterval() || "5",
      updatedAt: Date.now(),
    };
    layouts.push(lay);
    activeLayoutId = lay.id;
    persistLayouts();
    suppressAutosave = true;
    host.setGrid(lay.grid, { silent: true });
    var seed = syms.sector || syms.stock;
    if (seed && panesForGrid(lay.grid).length > 1) host.fillVisiblePanes(seed);
    suppressAutosave = false;
    renderLayoutChips();
    updateSettingsMenuHead();
    setTimeout(function () {
      saveActiveLayout(false);
    }, 600);
    status("Created " + n);
  }

  function activateLayout(id, opts) {
    opts = opts || {};
    var lay = null;
    for (var i = 0; i < layouts.length; i++) {
      if (layouts[i].id === id) {
        lay = layouts[i];
        break;
      }
    }
    if (!lay) return;
    activeLayoutId = lay.id;
    persistLayouts();
    suppressAutosave = true;
    if (lay.interval) host.setInterval(lay.interval);
    host.setGrid(lay.grid || "1", { silent: true });
    host.setActivePane(lay.activePane || "sector");
    var extras = Array.isArray(lay.extras) ? lay.extras : [];
    var boot = !!opts.fromBoot;
    var sectorState = lay.sectorChart || null;
    var sectorSym = lay.sector || host.getSymbols().sector || null;
    var linkSym = boot ? urlSymbol() : "";
    var laySyms = [sectorSym, lay.stock].concat(extras).map(function (s) {
      return String(s || "").toUpperCase();
    });
    if (linkSym && laySyms.indexOf(linkSym.toUpperCase()) < 0) {
      sectorState = sectorSym ? retargetChartState(sectorState, sectorSym, linkSym) : null;
      sectorSym = linkSym;
    }

    // Boot: open symbols first WITHOUT loading chart state, then apply state once.
    // (Loading in openOnPane + applyChartStates caused a double flicker.)
    if (boot) {
      if (sectorSym) host.openOnPane(sectorSym, "sector", { skipAutosave: true });
      if (lay.stock) host.openOnPane(lay.stock, "stock", { skipAutosave: true });
      for (var ei = 0; ei < 6; ei++) {
        if (extras[ei]) {
          host.openOnPane(extras[ei], "extra" + ei, { skipAutosave: true });
        }
      }
      host.applyChartStates(
        {
          sectorChart: sectorState,
          stockChart: lay.stockChart,
          extraCharts: lay.extraCharts || [],
        },
        480
      );
      setTimeout(function () {
        suppressAutosave = false;
      }, 1100);
    } else {
      if (sectorSym) {
        host.openOnPane(sectorSym, "sector", { skipAutosave: true, savedState: sectorState });
      } else if (sectorState) {
        host.applyChartStates({ sectorChart: sectorState }, 200);
      }
      if (lay.stock) host.openOnPane(lay.stock, "stock", { skipAutosave: true, savedState: lay.stockChart });
      for (var ej = 0; ej < 6; ej++) {
        if (extras[ej]) {
          host.openOnPane(extras[ej], "extra" + ej, {
            skipAutosave: true,
            savedState: (lay.extraCharts && lay.extraCharts[ej]) || null,
          });
        }
      }
      host.applyChartStates(
        {
          sectorChart: lay.sectorChart,
          stockChart: lay.stockChart,
          extraCharts: lay.extraCharts || [],
        },
        400
      );
      setTimeout(function () {
        suppressAutosave = false;
      }, 800);
    }
    renderLayoutChips();
    updateSettingsMenuHead();
    status(lay.name || "Layout");
  }

  function renameActiveLayout(name) {
    var lay = activeLayout();
    if (!lay) return;
    var n = String(name || "").trim();
    if (!n) return;
    lay.name = n;
    lay.updatedAt = Date.now();
    persistLayouts();
    renderLayoutChips();
    updateSettingsMenuHead();
  }

  function deleteActiveLayout() {
    if (!activeLayoutId) return;
    var id = activeLayoutId;
    layouts = layouts.filter(function (l) {
      return l.id !== id;
    });
    activeLayoutId = layouts.length ? layouts[0].id : null;
    persistLayouts();
    renderLayoutChips();
    updateSettingsMenuHead();
    if (activeLayoutId) activateLayout(activeLayoutId);
    else status("No active layout");
  }

  function closeActiveLayout() {
    activeLayoutId = null;
    localStorage.removeItem(ACTIVE_LAYOUT_KEY);
    renderLayoutChips();
    updateSettingsMenuHead();
    status("Layout closed");
  }

  function bindUi() {
    if (layoutChipsEl) {
      layoutChipsEl.addEventListener("click", function (e) {
        var btn = e.target.closest("[data-layout-id]");
        if (!btn) return;
        activateLayout(btn.getAttribute("data-layout-id"));
      });
    }
    if (settingsBtn) {
      settingsBtn.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (settingsMenu && settingsMenu.classList.contains("is-open")) closeSettingsMenu();
        else openSettingsMenu();
      });
    }
    if (settingsMenu) {
      settingsMenu.addEventListener("click", function (e) {
        e.stopPropagation();
      });
    }
    if (settingsBackdrop) settingsBackdrop.addEventListener("click", closeSettingsMenu);

    var saveBtn = document.getElementById("chartsSettingsSave");
    if (saveBtn) {
      saveBtn.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        ensureDefaultLayout();
        saveActiveLayout(false);
        closeSettingsMenu();
      });
    }
    var autoToggleBtn = document.getElementById("chartsSettingsAutoToggle");
    if (autoToggleBtn) {
      autoToggleBtn.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        // Strict: cannot turn off — keep ON and confirm with popup.
        setAutoSave(true);
        showAutosaveBadge("Auto ON");
        status("Auto save always ON");
      });
    }
    var renameBtn = document.getElementById("chartsSettingsRename");
    if (renameBtn) {
      renameBtn.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (!activeLayout()) {
          status("No layout to rename — create one first");
          closeSettingsMenu();
          return;
        }
        if (renameInput) renameInput.value = activeLayout().name || "";
        closeSettingsMenu();
        openModal(renameModal);
        setTimeout(function () {
          if (renameInput) renameInput.focus();
        }, 30);
      });
    }
    var deleteBtn = document.getElementById("chartsSettingsDelete");
    if (deleteBtn) {
      deleteBtn.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (!activeLayout()) {
          status("No layout to delete — create one first");
          closeSettingsMenu();
          return;
        }
        if (window.confirm('Delete layout "' + (activeLayout().name || "") + '"?')) {
          deleteActiveLayout();
        }
        closeSettingsMenu();
      });
    }
    var closeBtn = document.getElementById("chartsSettingsClose");
    if (closeBtn) {
      closeBtn.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        closeActiveLayout();
        closeSettingsMenu();
      });
    }
    if (newLayoutBtn) {
      newLayoutBtn.addEventListener("click", function (e) {
        e.preventDefault();
        e.stopPropagation();
        closeSettingsMenu();
        openNewLayoutModal();
      });
    }
    var cancelNew = document.getElementById("chartsNewLayoutCancel");
    if (cancelNew) {
      cancelNew.addEventListener("click", function () {
        closeModal(newLayoutModal);
      });
    }
    var createNew = document.getElementById("chartsNewLayoutCreate");
    if (createNew) {
      createNew.addEventListener("click", function () {
        createLayout(newLayoutName && newLayoutName.value, newLayoutGridId);
        closeModal(newLayoutModal);
      });
    }
    var cancelRename = document.getElementById("chartsRenameCancel");
    if (cancelRename) {
      cancelRename.addEventListener("click", function () {
        closeModal(renameModal);
      });
    }
    var saveRename = document.getElementById("chartsRenameSave");
    if (saveRename) {
      saveRename.addEventListener("click", function () {
        renameActiveLayout(renameInput && renameInput.value);
        closeModal(renameModal);
      });
    }
  }

  loadLayouts();
  bindUi();
  renderLayoutChips();
  updateSettingsMenuHead();
  renderGridPresetButtons(settingsGridPresets, host.getGrid(), function (g) {
    host.setGrid(g);
    scheduleAutosave();
  });

  // Always restore active layout chart state on boot when anything was saved.
  var lay = activeLayout();
  if (lay) {
    var hasExtras =
      Array.isArray(lay.extras) &&
      lay.extras.some(function (s) {
        return !!s;
      });
    var hasCharts =
      !!lay.sectorChart ||
      !!lay.stockChart ||
      (Array.isArray(lay.extraCharts) &&
        lay.extraCharts.some(function (s) {
          return !!s;
        }));
    var needsRestore =
      hasCharts ||
      hasExtras ||
      !!lay.stock ||
      (lay.grid && lay.grid !== "1") ||
      (lay.activePane && lay.activePane !== "sector");
    if (needsRestore || hasCharts) {
      setTimeout(function () {
        try {
          activateLayout(lay.id, { fromBoot: true });
        } catch (_) {}
      }, 280);
    } else {
      suppressAutosave = true;
      host.setGrid(lay.grid || "1", { silent: true });
      setTimeout(function () {
        suppressAutosave = false;
      }, 600);
    }
  }

  // Quiet boot ack in header only (no chart overlay).
  setTimeout(function () {
    showAutosaveBadge("Auto ON");
  }, 1100);

  return {
    scheduleAutosave: scheduleAutosave,
    notifySaved: notifySaved,
    saveActiveLayout: saveActiveLayout,
    ensureDefaultLayout: ensureDefaultLayout,
    activeLayout: activeLayout,
    activateLayout: activateLayout,
    getActiveLayoutId: function () {
      return activeLayoutId;
    },
    isAutoSaveOn: function () {
      return autoSaveOn;
    },
    suppress: function (on) {
      suppressAutosave = !!on;
    },
    panesForGrid: panesForGrid,
    MAX_LAYOUTS: MAX_LAYOUTS,
  };
}
